import * as React from 'react'
import { colors, CTAPrimary } from '@components/shared'
import {
  StyledInput,
  Form,
  ErrorMessage,
  ErrorMessageContainer
} from '@components/shared/formElements'
import styled from 'styled-components'
import { H1, P } from '@components/shared/typography'
import LoadingDotsAnimation from '@components/shared/LoadingDotsAnimation'
import { useDispatch, useSelector } from 'react-redux'
import type { AppStateType } from '@reducers/appstate'
import { useRouter } from 'next/router'
import { saveBlogEmail } from '../../actions/userActions'
import { events } from '../../services/analytics'

const captureText = {
  intro: 'Stay in touch',
  header: 'Never miss a moment',
  body: 'New stories drop weekly in the Dipsea app! Sign up to make sure you don\u2019t miss out.',
  submit: 'Notify Me'
}

const successText = {
  header: 'All Set!',
  body: 'We sent you an email. Check your inbox to get started on your Dipsea journey.'
}

export default function HomeEmailCapture (): React.ReactNode {
  const dispatch = useDispatch()
  const blogEmailErrorText = useSelector((state: AppStateType): string | null | undefined => state.blogEmailErrorText)
  const blogEmailIsLoading = useSelector((state: AppStateType): boolean => state.blogEmailIsLoading)
  const blogEmailIsSuccess = useSelector((state: AppStateType): boolean => state.blogEmailIsSuccess)

  const router = useRouter()
  const asPath = router.asPath || 'unknown'

  const [email, setEmail] = React.useState('')
  const [errorMessage, setErrorMessage] = React.useState(blogEmailErrorText)
  const [isLoading, setIsLoading] = React.useState(blogEmailIsLoading)
  const [showSuccess, setShowSuccess] = React.useState(false)

  React.useEffect((): void => {
    setIsLoading(blogEmailIsLoading)
  }, [blogEmailIsLoading])

  React.useEffect((): void => {
    setErrorMessage(blogEmailErrorText)
  }, [blogEmailErrorText])

  React.useEffect((): void => {
    events.feature_used({ name: 'home_email_capture_shown' })
  }, [])

  React.useEffect((): void => {
    if (blogEmailIsSuccess) {
      events.feature_used({ name: 'home_email_capture_success' })
      setShowSuccess(true)
    }
  }, [blogEmailIsSuccess])

  const isEmailValid = (): boolean => {
    if (!email) return false
    return /\S+@\S+\.\S+/.test(email)
  }

  const handleEmailChange = (e: any): void => {
    setEmail(e.target.value.replace(/\s+/g, ''))
    setErrorMessage(undefined)
  }

  const handleSubmit = (e: any): void => {
    e.preventDefault()
    if (isLoading) return

    if (!email) {
      setErrorMessage('Enter an email address to claim your promo')
      return
    }

    if (!isEmailValid()) {
      setErrorMessage('There\u2019s a typo in your email address')
      return
    }

    events.feature_used({ name: 'home_email_capture_submitted' })
    setIsLoading(true)
    dispatch(saveBlogEmail(email.toLowerCase(), asPath))
    try {
      window.localStorage.setItem('dipsea_blog_status', 'success')
      window.localStorage.setItem('dipsea_blog_date', String(Date.now()))
    } catch (e) {}
  }

  if (showSuccess) {
    return (
      <Container>
        <Header>{successText.header}</Header>
        <Body>{successText.body}</Body>
      </Container>
    )
  }

  return (
    <Container>
      <Intro>{captureText.intro}</Intro>
      <Header>{captureText.header}</Header>
      <Body>{captureText.body}</Body>

      <FormWrapper onSubmit={handleSubmit}>
        <ErrorMessageContainer>
          <ErrorMessage showError={!!errorMessage}>
            {errorMessage || ''}
          </ErrorMessage>
        </ErrorMessageContainer>

        <StyledInput
          name='email'
          type='email'
          placeholder='Email'
          value={email}
          onChange={handleEmailChange}
          valid={!errorMessage}
          invertColor
          styles={{ width: '100%' }}
          color={colors.sand}
          borderColor={colors.sand}
        />

        <SubmitButton disabled={isLoading}>
          {isLoading
            ? <LoadingDotsAnimation color={colors.sand} translucentColor={colors.sand20} />
            : captureText.submit}
        </SubmitButton>
      </FormWrapper>

    </Container>
  )
}

const Container = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
`

const Intro = styled(P)<any>`
  color: ${colors.sand};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 18px;
  margin: 0 0 12px;
  padding: 0;
  text-align: center;
  text-transform: uppercase;
`

const Header = styled(H1)<any>`
  color: ${colors.sand};
  font-size: 45px;
  line-height: 54px;
  margin: 0 0 16px;
  padding: 0;
  text-align: center;
  white-space: pre-wrap;
`

const Body = styled(P)<any>`
  color: ${colors.sand};
  font-size: 14px;
  font-weight: 300;
  line-height: 21px;
  margin: 0 0 10px;
  padding: 0;
  text-align: center;
`

const FormWrapper = styled(Form)<any>`
  width: 100%;
`

const SubmitButton = styled(CTAPrimary)<any>`
  margin: 24px 0 16px;
  min-height: 40px;
  padding: 10px;
  width: 100%;
  height: unset;
`
